import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Modal } from '@/components/Modal';
import {
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  putVariant,
  deleteVariant,
  uploadVariantImage,
  type PushTemplate,
  type PushVariant,
} from '@/api/push';

const mediaBase = (import.meta.env.VITE_API_URL || window.location.origin)
  .replace(/\/api\/v1\/?$/, '');

function imageUrl(key: string) {
  return `${mediaBase}/media/${key}`;
}

// ─── New Template Modal ───────────────────────────────────────────────────────

function NewTemplateModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('en');
  const [deeplink, setDeeplink] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      createTemplate({ name: name.trim(), fallback_locale: locale.trim() || 'en', default_deeplink: deeplink.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-templates'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); setError(''); mut.mutate(); }}
      className="space-y-4"
    >
      {error && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Name</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. daily-promo"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Fallback locale</label>
        <input
          type="text"
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          placeholder="en"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Default deeplink</label>
        <input
          type="text"
          value={deeplink}
          onChange={(e) => setDeeplink(e.target.value)}
          placeholder="myapp://home"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" loading={mut.isPending} disabled={!name.trim()}>Create</Button>
      </div>
    </form>
  );
}

// ─── Variant Tab Editor ───────────────────────────────────────────────────────

function VariantEditor({
  templateId,
  locale,
  variant,
  onSaved,
  onDeleted,
}: {
  templateId: string;
  locale: string;
  variant: PushVariant | undefined;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState(variant?.title ?? '');
  const [body, setBody] = useState(variant?.body ?? '');
  const [deeplink, setDeeplink] = useState(variant?.deeplink ?? '');
  const [imageKey, setImageKey] = useState(variant?.image_r2_key ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      putVariant(templateId, locale, {
        title: title.trim(),
        body: body.trim(),
        image_r2_key: imageKey || null,
        deeplink: deeplink.trim() || null,
      }),
    onSuccess: () => { setSaveError(''); onSaved(); },
    onError: (err: Error) => setSaveError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteVariant(templateId, locale),
    onSuccess: onDeleted,
    onError: (err: Error) => setSaveError(err.message),
  });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setUploadError('');
    try {
      const res = await uploadVariantImage(templateId, locale, file);
      setImageKey(res.image_r2_key);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      {saveError && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{saveError}</div>}

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Notification title"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Body</label>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Notification body text"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Deeplink override</label>
        <input
          type="text"
          value={deeplink}
          onChange={(e) => setDeeplink(e.target.value)}
          placeholder="myapp://promo (optional)"
          className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Image</label>
        {imageKey ? (
          <div className="space-y-2">
            <img src={imageUrl(imageKey)} alt="variant" className="h-28 rounded-md border border-border object-cover" />
            <p className="text-xs text-content-subtle font-mono truncate">{imageKey}</p>
            <button
              type="button"
              onClick={() => setImageKey('')}
              className="text-xs text-danger hover:underline"
            >
              Remove image
            </button>
          </div>
        ) : (
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2 text-sm border border-border-strong rounded-md hover:bg-surface-muted disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Upload image'}
            </button>
            {uploadError && <p className="mt-1 text-xs text-danger">{uploadError}</p>}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Button
          type="button"
          variant="secondary"
          onClick={() => { if (confirm(`Delete variant "${locale}"?`)) deleteMut.mutate(); }}
          loading={deleteMut.isPending}
        >
          Delete variant
        </Button>
        <Button
          type="button"
          onClick={() => saveMut.mutate()}
          loading={saveMut.isPending}
          disabled={!title.trim() || !body.trim()}
        >
          Save variant
        </Button>
      </div>
    </div>
  );
}

// ─── Edit Template Modal ──────────────────────────────────────────────────────

function EditTemplateModal({ template, onClose }: { template: PushTemplate; onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['push-template', template.id],
    queryFn: () => getTemplate(template.id),
  });

  const variants = data?.variants ?? [];

  const [name, setName] = useState(template.name);
  const [fallbackLocale, setFallbackLocale] = useState(template.fallback_locale);
  const [defaultDeeplink, setDefaultDeeplink] = useState(template.default_deeplink ?? '');
  const [metaError, setMetaError] = useState('');
  const [activeLocale, setActiveLocale] = useState<string | null>(null);
  const [newLocale, setNewLocale] = useState('');
  const [addingLocale, setAddingLocale] = useState(false);

  const locales = variants.map((v) => v.locale);
  const current = activeLocale ?? locales[0] ?? null;

  const metaMut = useMutation({
    mutationFn: () =>
      updateTemplate(template.id, {
        name: name.trim(),
        fallback_locale: fallbackLocale.trim() || 'en',
        default_deeplink: defaultDeeplink.trim() || null,
      } as Partial<PushTemplate>),
    onSuccess: () => {
      setMetaError('');
      queryClient.invalidateQueries({ queryKey: ['push-templates'] });
    },
    onError: (err: Error) => setMetaError(err.message),
  });

  const inval = () => queryClient.invalidateQueries({ queryKey: ['push-template', template.id] });

  const handleAddLocale = () => {
    const loc = newLocale.trim().toLowerCase();
    if (!loc || locales.includes(loc)) return;
    setActiveLocale(loc);
    setAddingLocale(false);
    setNewLocale('');
  };

  return (
    <div className="space-y-5 min-h-[400px]">
      {/* Metadata section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Metadata</h3>
        {metaError && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{metaError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Fallback locale</label>
            <input
              type="text"
              value={fallbackLocale}
              onChange={(e) => setFallbackLocale(e.target.value)}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Default deeplink</label>
            <input
              type="text"
              value={defaultDeeplink}
              onChange={(e) => setDefaultDeeplink(e.target.value)}
              placeholder="myapp://home"
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={() => metaMut.mutate()} loading={metaMut.isPending}>
            Save metadata
          </Button>
        </div>
      </div>

      {/* Locale variant tabs */}
      <div>
        <div className="flex items-center gap-1 border-b border-border mb-3 flex-wrap">
          {isLoading && <span className="text-xs text-content-subtle py-2 px-2">Loading…</span>}
          {locales.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => { setActiveLocale(loc); setAddingLocale(false); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                current === loc && !addingLocale
                  ? 'border-primary text-primary'
                  : 'border-transparent text-content-subtle hover:text-content-muted'
              }`}
            >
              {loc}
            </button>
          ))}
          {addingLocale ? (
            <div className="flex items-center gap-1 pb-1">
              <input
                autoFocus
                type="text"
                value={newLocale}
                onChange={(e) => setNewLocale(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLocale(); } if (e.key === 'Escape') { setAddingLocale(false); setNewLocale(''); } }}
                placeholder="e.g. de"
                className="w-20 px-2 py-1 border border-border-strong rounded text-sm focus:outline-none focus:ring-primary focus:border-primary"
              />
              <button type="button" onClick={handleAddLocale} className="text-xs text-primary hover:underline">Add</button>
              <button type="button" onClick={() => { setAddingLocale(false); setNewLocale(''); }} className="text-xs text-content-subtle hover:underline">Cancel</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingLocale(true)}
              className="px-3 py-2 text-sm text-content-subtle hover:text-primary border-b-2 border-transparent"
            >
              + Add locale
            </button>
          )}
        </div>

        {(current || addingLocale) && !isLoading && (
          <VariantEditor
            key={current ?? '__new__'}
            templateId={template.id}
            locale={current ?? newLocale}
            variant={variants.find((v) => v.locale === current)}
            onSaved={() => { inval(); queryClient.invalidateQueries({ queryKey: ['push-templates'] }); }}
            onDeleted={() => { setActiveLocale(null); inval(); queryClient.invalidateQueries({ queryKey: ['push-templates'] }); }}
          />
        )}
        {!current && !addingLocale && !isLoading && (
          <p className="text-sm text-content-subtle py-4">No variants yet. Add a locale to get started.</p>
        )}
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PushTemplates() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['push-templates'],
    queryFn: listTemplates,
  });
  const templates = data?.templates ?? [];

  const [showNew, setShowNew] = useState(false);
  const [editTemplate, setEditTemplate] = useState<PushTemplate | null>(null);

  const deleteMut = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['push-templates'] }),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Push · Templates</h1>
          <p className="text-sm text-content-subtle mt-1">Manage notification templates with per-locale variants.</p>
        </div>
        <EditOnly><Button onClick={() => setShowNew(true)}>New template</Button></EditOnly>
      </div>

      {isLoading ? (
        <div className="text-sm text-content-subtle">Loading…</div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-xs text-content-subtle uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Fallback locale</th>
                <th className="text-left px-4 py-3">Variants</th>
                <th className="text-left px-4 py-3">Default deeplink</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {templates.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-3 font-medium text-content">{t.name}</td>
                  <td className="px-4 py-3 text-content-muted">{t.fallback_locale}</td>
                  <td className="px-4 py-3 text-content-muted">{t.variant_count ?? '—'}</td>
                  <td className="px-4 py-3 text-content-subtle font-mono text-xs max-w-xs truncate">
                    {t.default_deeplink ?? <span className="text-content-subtle">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                    <EditOnly>
                      <button onClick={() => setEditTemplate(t)} className="text-xs text-primary hover:underline">Edit</button>
                      <button
                        onClick={() => { if (confirm(`Delete template "${t.name}"?`)) deleteMut.mutate(t.id); }}
                        className="text-xs text-danger hover:underline"
                      >
                        Delete
                      </button>
                    </EditOnly>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-content-subtle text-sm" colSpan={5}>
                    No templates yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* New template modal */}
      <Modal isOpen={showNew} onClose={() => setShowNew(false)} title="New template">
        <NewTemplateModal onClose={() => setShowNew(false)} />
      </Modal>

      {/* Edit template modal */}
      <Modal
        isOpen={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        title={editTemplate ? `Edit template · ${editTemplate.name}` : ''}
      >
        {editTemplate && (
          <EditTemplateModal template={editTemplate} onClose={() => setEditTemplate(null)} />
        )}
      </Modal>
    </div>
  );
}
